/**
 * YTAN — den återvändande vita ytan (`.block` i prototypen). Nästan allt
 * innehåll i appen står på en.
 *
 * REGELN ÄGAREN SATT: ytan är RENVIT (--surface), lyft med SKUGGA och aldrig
 * med en ram. Gradienten ligger bara på botten, bakom korten. Ingen rosa
 * fyllning, ingen rosa ram.
 *
 * Här bor också de tre små sakerna som alltid står på en yta: sifferblocket,
 * inställningsraden och tomtexten.
 */

import type { ReactNode } from 'react';
import { Ikon } from './Ikon';
import type { Ikonnamn } from './Ikon';

export interface Ytaprops {
  /** Rubriken i ytans huvud (`.bt`). Utan den ritas inget huvud alls. */
  readonly rubrik?: ReactNode;
  /**
   * Talet till höger i huvudet (`.bc`) — "3 st", "42,0 mil", "2 / 7".
   * Sätts i --mono med tabular-nums, så listor står i pelare.
   */
  readonly rakning?: ReactNode;
  /** Ikon före rubriken. Ritas i --accent. */
  readonly ikon?: Ikonnamn;
  /** Tyst variant: ingen skugga, en hårfin ram. För sekundärt innehåll. */
  readonly tyst?: boolean;
  readonly children?: ReactNode;
}

export const Yta = ({ rubrik, rakning, ikon, tyst = false, children }: Ytaprops) => (
  <div className={tyst ? 'block k-tyst' : 'block'}>
    {(rubrik !== undefined || rakning !== undefined) && (
      <div className="block-h">
        {ikon !== undefined && <Ikon namn={ikon} storlek={18} />}
        {rubrik !== undefined && <span className="bt">{rubrik}</span>}
        {rakning !== undefined && <span className="bc">{rakning}</span>}
      </div>
    )}
    {children}
  </div>
);

/* ------------------------------------------------------------------------ */

export interface Sifferblockprops {
  /**
   * Talet. FORMATERA DET INNAN — 7,5 h, 1 050 kr, 42,0 mil. Komponenten får
   * aldrig ett rakt öre- eller minuttal: enheterna i shared/types.ts är
   * heltal i minsta enhet (450 minuter, 105000 ören) och de talen är inte
   * mänsklig text.
   */
  readonly varde: ReactNode;
  /** Raden under: vad talet är och vilken period det gäller. */
  readonly under?: ReactNode;
}

/** `.kpi` — det stora talet på mil-, utläggs- och fakturaskärmarna. */
export const Sifferblock = ({ varde, under }: Sifferblockprops) => (
  <p className="kpi">
    {varde}
    {under !== undefined && <small>{under}</small>}
  </p>
);

/**
 * TVÅ SIFFERBLOCK SIDA VID SIDA, utan gemensam total.
 *
 * BESLUT 05: fakturaunderlaget visar attesterat SKILT från oattesterat, och de
 * får ALDRIG summeras ihop. Komponenten har därför plats för exakt två block
 * och ingen summarad — den formen är själv regeln.
 */
export const Sifferpar = ({
  vanster,
  hoger,
}: {
  readonly vanster: ReactNode;
  readonly hoger: ReactNode;
}) => (
  <div className="k-siffror">
    <div className="block">{vanster}</div>
    <div className="block">{hoger}</div>
  </div>
);

/* ------------------------------------------------------------------------ */

export interface Installningsradprops {
  readonly ikon?: Ikonnamn;
  /** "Räknas från" */
  readonly etikett: ReactNode;
  /** "hemadressen" — visas i --display, halvfet. */
  readonly varde: ReactNode;
  /** Knappen till höger, oftast en <Knapp variant="piller-mini">Ändra</Knapp>. */
  readonly atgard?: ReactNode;
}

/** `.setting` — en dämpad rad som visar ett val och låter ändra det. */
export const Installningsrad = ({ ikon, etikett, varde, atgard }: Installningsradprops) => (
  <div className="setting">
    {ikon !== undefined && <Ikon namn={ikon} storlek={17} />}
    <span>
      {etikett} <b>{varde}</b>
    </span>
    {atgard !== undefined && <span style={{ marginLeft: 'auto' }}>{atgard}</span>}
  </div>
);

/* ------------------------------------------------------------------------ */

/**
 * `.empty` — den dämpade texten under en lista.
 *
 * TVÅ UPPGIFTER, och de ser likadana ut: "det finns inget än" och "så här
 * fungerar det här". Prototypen använder den mest för det andra — den korta
 * förklaringen längst ner på varje skärm som säger VARFÖR saken är som den är.
 * Ta inte bort dem: de är produktens enda hjälptext.
 */
export const Tomtext = ({ children }: { readonly children: ReactNode }) => (
  <p className="empty">{children}</p>
);
